import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HiChevronDown, HiX } from 'react-icons/hi';
import { TbPlayerTrackNext, TbPlayerTrackPrev, TbPlayerPlay, TbPlayerPause } from "react-icons/tb";
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { useDarkMode } from '../../../contexts/DarkModeContext';
import { isAndroid } from '../../../utils/platformDetection';

const slideUpAnimation = "animate-slide-up";
const slideDownAnimation = "animate-slide-down";

// Helper function to detect iPad Air 5th generation in compatibility mode
const isIpadAir5thGenCompatMode = () => {
  const userAgent = navigator.userAgent;
  
  // Check for iPad in the user agent
  const isIPad = /iPad/.test(userAgent) || 
                (/Macintosh/.test(userAgent) && 'ontouchend' in document);
  
  // Check for iPadOS 18.4.1 specifically mentioned in the user query
  const iPadOS18Match = userAgent.match(/OS 18_4(_1)?/);
  
  // Check for iPadOS 17+ which is used on iPad Air 5th gen
  const iPadOSMatch = userAgent.match(/OS (\d+)_/);
  const isRecentIPadOS = iPadOSMatch && parseInt(iPadOSMatch[1], 10) >= 17;
  
  // Check for compatibility view
  const isCompatibilityMode = !window.navigator.standalone && 
                             window.matchMedia('(display-mode: browser)').matches;
  
  // Check screen dimensions typical for iPad Air 5th gen (2360x1640)
  const hasExpectedScreenSize = 
    (window.screen.width >= 820 && window.screen.width <= 1180) && 
    (window.screen.height >= 1080 && window.screen.height <= 1640);
  
  // If we specifically detect iPadOS 18.4.1, prioritize that
  if (isIPad && iPadOS18Match) {
    return true;
  }
  
  return isIPad && isCompatibilityMode && (isRecentIPadOS || hasExpectedScreenSize);
};

const MobilePlayer = ({
  surahContent,
  currentVerse,
  selectedSurah,
  surahs,
  isPlaying,
  togglePlay,
  setCurrentVerse,
  footnotes,
  setActiveFootnote,
  activeFootnote,
  isDarkMode,
  selectedTranslation,
  audioDuration,
  audioProgress,
  handleVerseClick
}) => {
  const [isPlayerExpanded, setIsPlayerExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isIpadAir5, setIsIpadAir5] = useState(false);
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const userScrollTimeoutRef = useRef(null);

  // Check for iPad Air 5th gen on component mount
  useEffect(() => {
    const detected = isIpadAir5thGenCompatMode();
    setIsIpadAir5(detected);
    
    // Debug logging
    if (detected) {
      console.log('iPad Air 5th generation in compatibility mode detected');
      console.log('User Agent:', navigator.userAgent);
      console.log('Screen dimensions:', window.screen.width, 'x', window.screen.height);
    }
  }, []);

  // Handle play button click with error handling
  const handlePlayClick = (e) => {
    // For iPadOS compatibility mode
    if (e) {
      e.preventDefault(); // Prevent default
      e.stopPropagation(); // Prevent event bubbling
    }
    
    // Call the parent component's togglePlay function
    togglePlay();
  };

  // Handle navigation clicks
  const handlePrevVerse = useCallback(() => {
    if (currentVerse > 0) {
      setCurrentVerse(currentVerse - 1);
    }
  }, [currentVerse, setCurrentVerse]);

  const handleNextVerse = useCallback(() => {
    if (currentVerse < surahContent.length - 1) {
      setCurrentVerse(currentVerse + 1);
    }
  }, [currentVerse, surahContent, setCurrentVerse]);

  // Reset scroll position when verse changes or player expands
  useEffect(() => {
    setScrollPosition(0);
    setIsUserScrolling(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [currentVerse, isPlayerExpanded]);

  // Handle scroll based on audio progress
  useEffect(() => {
    if (isPlaying && isPlayerExpanded && contentRef.current && !isUserScrolling) {
      const content = contentRef.current;
      const totalScrollHeight = content.scrollHeight - content.clientHeight;
      
      // Calculate scroll position based on audio progress
      const newScrollPosition = (audioProgress / 100) * totalScrollHeight;
      
      // Use requestAnimationFrame for smoother scrolling
      const smoothScroll = () => {
        const currentScroll = content.scrollTop;
        const diff = newScrollPosition - currentScroll;
        
        // If we're close enough, just set to final position
        if (Math.abs(diff) < 1) {
          content.scrollTop = newScrollPosition;
          return;
        }
        
        // Smooth step towards target
        const step = diff * 0.1;
        content.scrollTop = currentScroll + step;
        
        if (isPlaying && !isUserScrolling) {
          requestAnimationFrame(smoothScroll);
        }
      };
      
      requestAnimationFrame(smoothScroll);
    }
  }, [isPlaying, isPlayerExpanded, audioProgress, isUserScrolling]);

  // Handle user scroll interaction
  const handleScroll = () => {
    if (!isUserScrolling) {
      setIsUserScrolling(true);
    }

    // Clear existing timeout
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }

    // Reset user scrolling flag after 2 seconds of no scroll activity
    userScrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 2000);
  };

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsPlayerExpanded(false);
    }, 300);
  };

  // Log when component renders with important props
  useEffect(() => {
    console.log('MobilePlayer rendered with:', { 
      currentVerse, 
      surahContentLength: surahContent?.length,
      setCurrentVerseType: typeof setCurrentVerse 
    });
  }, [currentVerse, surahContent, setCurrentVerse]);

  if (!surahContent) return null;

  return (
    <>
      {!isPlayerExpanded && (
        <div 
          className={`fixed left-0 right-0 backdrop-blur-xl px-6 py-2 z-40 cursor-pointer transform transition-all duration-300 ${
            isDarkMode
              ? 'bg-black/25 border-t border-white/10'
              : 'bg-white/70 border-t border-emerald-100'
          } ${slideUpAnimation} ${isIpadAir5 ? 'ipad-air-5-compat' : ''}`}
          style={{
            bottom: (() => {
              if (isIpadAir5) {
                // Adjust position higher for iPad Air 5th gen in compatibility mode
                return 'calc(env(safe-area-inset-bottom) + 65px)';
              } else if (window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches) {
                const baseBottom = 'calc(env(safe-area-inset-bottom) + 3.2rem)';
                return isAndroid() ? `calc(${baseBottom} + 0.75rem)` : baseBottom;
              } else if (navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')) {
                return 'calc(env(safe-area-inset-bottom) + 50px)';
              } else {
                return isAndroid() ? 'calc(4rem + 0.75rem)' : '4rem';
              }
            })()
          }}
          onClick={(e) => {
            e.preventDefault();
            setIsPlayerExpanded(true);
          }}
        >
          <div className="flex items-center justify-between px-2">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <button 
                className="text-green-600 dark:text-green-400 p-2" 
                onClick={handlePlayClick}
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                {isPlaying ? 
                  <TbPlayerPause className="h-6 w-6" /> : 
                  <TbPlayerPlay className="h-6 w-6" />
                }
              </button>
              <div className="truncate">
                <div className="text-sm font-medium truncate dark:text-white">
                  {surahs.find(s => s.id.toString() === selectedSurah)?.name_simple || 'No surah selected'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {surahContent && surahContent[currentVerse] ? `Verse ${surahContent[currentVerse].verse_key.split(':')[1]}` : ''}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isPlayerExpanded && (
        <div 
          className={`fixed inset-0 z-[60] flex flex-col transform transition-all duration-300 ${
            isClosing ? slideDownAnimation : slideUpAnimation
          } ${isDarkMode ? 'bg-gray-950' : 'bg-white'}`}
          style={{
            paddingBottom: window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches
              ? 'calc(env(safe-area-inset-bottom) + 3.2rem)'
              : navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')
                ? 'calc(env(safe-area-inset-bottom) + 50px)'
                : '4rem'
          }}
        >
          {/* Background layers */}
          {isDarkMode ? (
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-gray-950/50 to-gray-950" />
          ) : (
            <div className="absolute inset-0">
              {/* Main gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50"></div>
              
              {/* Subtle emerald accent in corners */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-emerald-500/5 to-transparent rounded-bl-full"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-emerald-500/5 to-transparent rounded-tr-full"></div>
              
              {/* Very subtle pattern overlay */}
              <div className="absolute inset-0 opacity-[0.02]" 
                   style={{
                     backgroundImage: 'url("/images/bgpattern.png")', 
                     backgroundSize: '400px',
                     filter: 'invert(1)', 
                     mixBlendMode: 'multiply'
                   }}>
              </div>
              
              {/* Subtle border around the entire player */}
              <div className="absolute inset-0 border border-gray-100"></div>
            </div>
          )}

          {/* Header section with close button */}
          <div className={`relative pt-[calc(env(safe-area-inset-top)_+_1rem)] px-4 pb-4 flex items-center backdrop-blur-sm ${
            isDarkMode 
              ? 'border-b border-white/10 bg-white/5'
              : 'border-b border-gray-100 bg-white/70'
          }`}>
            <button 
              onClick={handleClose}
              className={`transition-transform duration-200 hover:scale-110 relative z-10 ${
                isDarkMode
                  ? 'text-gray-300'
                  : 'text-gray-500'
              }`}
            >
              <HiChevronDown className="h-6 w-6" />
            </button>
            <div className="absolute left-0 right-0 text-center z-[1]">
              <div className={`text-sm font-medium ${
                isDarkMode
                  ? 'text-gray-300'
                  : 'text-gray-700'
              }`}>Now Playing</div>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col p-4 overflow-hidden animate-fade-in" ref={containerRef}>
            {selectedSurah && (
              <div className="flex flex-col items-center h-full">
                <div className="w-full flex flex-col items-center">
                  <div className="w-full flex justify-center -mt-8">
                    <img 
                      src={`/images/quran_chapters/Surah_${selectedSurah}_of_114${isDarkMode ? '_(modified)' : ''}.svg`}
                      alt={`Surah ${selectedSurah}`}
                      className="w-[55%] h-auto"
                    />
                  </div>
                  
                  <div className="text-center -mt-6">
                    <div className={`text-xs leading-tight ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Surah {selectedSurah}
                    </div>
                    <div className={`text-sm font-medium leading-tight ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                      {surahs.find(s => s.id.toString() === selectedSurah)?.name_simple}
                    </div>
                  </div>
                </div>

                <div className="flex-1 w-full flex flex-col mt-12 overflow-hidden">
                  {surahContent && surahContent[currentVerse] && (
                    <div 
                      ref={contentRef}
                      className="w-full h-full space-y-4 text-center overflow-y-auto scrollbar-none"
                      style={{
                        maskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
                        WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)'
                      }}
                      onScroll={handleScroll}
                    >
                      <div className="px-4 py-8">
                        <p 
                          key={`arabic-${currentVerse}`} 
                          className={`text-2xl leading-loose font-arabic transition-opacity duration-300 ease-in-out animate-fade-in ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}
                          dir="rtl"
                        >
                          {surahContent[currentVerse].text_uthmani}
                        </p>

                        <div 
                          key={`translation-${currentVerse}`}
                          className={`text-base px-4 mt-4 transition-opacity duration-300 ease-in-out animate-fade-in ${
                            isDarkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}
                        >
                          {surahContent[currentVerse].translations.find(t => 
                            t.resource_id.toString() === selectedTranslation
                          )?.text.split(/<sup.*?<\/sup>/).map((part, i, arr) => (
                            <React.Fragment key={i}>
                              {part}
                              {i < arr.length - 1 && (() => {
                                const footnoteMatch = surahContent[currentVerse].translations
                                  .find(t => t.resource_id.toString() === selectedTranslation)
                                  ?.text
                                  .split(/<sup.*?<\/sup>/)[i + 1]
                                  .match(/<sup foot_note=(\d+)>(\d+)<\/sup>/);
                                if (footnoteMatch) {
                                  const footnoteId = footnoteMatch[1];
                                  return (
                                    <sup
                                      className={`cursor-pointer ml-[1px] ${
                                        isDarkMode ? 'text-green-400' : 'text-emerald-600'
                                      }`}
                                      onClick={() => setActiveFootnote(
                                        activeFootnote === footnoteId ? null : footnoteId
                                      )}
                                    >
                                      [{footnoteMatch[2]}]
                                    </sup>
                                  );
                                }
                                return null;
                              })()}
                            </React.Fragment>
                          ))}
                        </div>
                        <div className="h-48" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Player controls */}
                <div 
                  className={`fixed bottom-0 left-0 right-0 w-full backdrop-blur-xl ${
                    isDarkMode 
                      ? 'border-t border-white/5 bg-black/30' 
                      : 'border-t border-gray-100 bg-white/70'
                  }`}
                  style={{
                    height: '120px',
                    transition: 'none', 
                    opacity: 1
                  }}
                >
                  <div className="flex items-center justify-center space-x-12 h-full">
                    <button 
                      className={`w-12 h-12 flex items-center justify-center transition-colors ${
                        isDarkMode
                          ? 'text-gray-400 hover:text-emerald-400'
                          : 'text-gray-500 hover:text-emerald-600'
                      } ${currentVerse <= 0 ? 'opacity-50' : ''}`}
                      onClick={handlePrevVerse}
                      disabled={currentVerse <= 0}
                      style={{
                        WebkitTapHighlightColor: 'transparent',
                        touchAction: 'manipulation'
                      }}
                    >
                      <SkipBack className="w-6 h-6" />
                    </button>

                    <button 
                      className={`w-16 h-16 flex items-center justify-center rounded-full backdrop-blur-sm border transition-all transform hover:scale-105 ${
                        isDarkMode
                          ? 'bg-emerald-950/50 border-emerald-400/10 text-emerald-400 hover:bg-emerald-900/30'
                          : 'bg-emerald-50 border-emerald-200 text-emerald-600 hover:bg-emerald-100'
                      }`}
                      style={{
                        boxShadow: isDarkMode 
                          ? '0 0 30px rgba(52, 211, 153, 0.1)'
                          : '0 0 30px rgba(16, 185, 129, 0.08)',
                        WebkitTapHighlightColor: 'transparent', // Remove tap highlight on iOS
                        touchAction: 'manipulation' // Optimize for touch
                      }}
                      onClick={handlePlayClick}
                    >
                      {isPlaying ? 
                        <Pause className="w-8 h-8" /> : 
                        <Play className="w-8 h-8 ml-1" />
                      }
                    </button>

                    <button 
                      className={`w-12 h-12 flex items-center justify-center transition-colors ${
                        isDarkMode
                          ? 'text-gray-400 hover:text-emerald-400'
                          : 'text-gray-500 hover:text-emerald-600'
                      } ${surahContent && currentVerse >= surahContent.length - 1 ? 'opacity-50' : ''}`}
                      onClick={handleNextVerse}
                      disabled={surahContent && currentVerse >= surahContent.length - 1}
                      style={{
                        WebkitTapHighlightColor: 'transparent',
                        touchAction: 'manipulation'
                      }}
                    >
                      <SkipForward className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(MobilePlayer, (prevProps, nextProps) => {
  // Return true if the props are equal (meaning component should NOT re-render)
  return (
    prevProps.currentVerse === nextProps.currentVerse &&
    prevProps.isPlaying === nextProps.isPlaying &&
    prevProps.audioProgress === nextProps.audioProgress &&
    prevProps.selectedSurah === nextProps.selectedSurah &&
    prevProps.surahContent?.length === nextProps.surahContent?.length &&
    prevProps.isDarkMode === nextProps.isDarkMode
  );
});
